import React, {
  useEffect, useState, useRef, useCallback,
} from 'react';
import {
  Svg, Circle, Line, Text as SvgText, G, Defs, RadialGradient, Stop, Marker, Path, Rect,
} from 'react-native-svg';
import {
  forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide, forceY, Simulation,
} from 'd3-force';
import {
  Box, VStack, Button, ButtonIcon, ButtonText, useToken,
} from '@gluestack-ui/themed';
import {
  ZoomIn, ZoomOut, Maximize, Layout,
} from 'lucide-react-native';
import { ConceptNode, ConceptEdge, ConceptCluster } from '@/common/api/models';

interface GraphVisualizerProps {
  nodes: ConceptNode[];
  edges: ConceptEdge[];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/no-unused-prop-types
  clusters: ConceptCluster[];
  selectedNodeIds: string[];
  onToggleNode: (id: string) => void;
  width: number;
  height: number;
}

interface SimulationNode extends ConceptNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number | null;
  fy?: number | null;
}

interface SimulationLink extends ConceptEdge {
  source: SimulationNode;
  target: SimulationNode;
}

export const ConceptGraphVisualizer: React.FC<GraphVisualizerProps> = ({
  nodes,
  edges,
  selectedNodeIds,
  onToggleNode,
  width,
  height,
}) => {
  // @ts-expect-error - brandBase is a semantic token
  const brandBase = useToken('colors', 'brandBase') as string;
  // @ts-expect-error - textSubtle is a semantic token
  const textSubtle = useToken('colors', 'textSubtle') as string;
  // @ts-expect-error - textHeading is a semantic token
  const textHeading = useToken('colors', 'textHeading') as string;

  const fontFamily = 'Inter, -apple-system, system-ui, sans-serif';

  const [simNodes, setSimNodes] = useState<SimulationNode[]>([]);
  const [simLinks, setSimLinks] = useState<SimulationLink[]>([]);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });

  const simulationRef = useRef<Simulation<SimulationNode, SimulationLink> | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<SimulationNode[]>([]);
  const isDraggingBackground = useRef(false);
  const draggedNode = useRef<SimulationNode | null>(null);
  const lastMousePos = useRef({ x: 0, y: 0 });

  // Initialize Simulation
  useEffect(() => {
    if (!nodes.length) return undefined;

    // Use existing positions if available to prevent "jumping" on re-initialization
    const existingNodeMap = new Map(nodesRef.current.map((n) => [n.id, n]));

    const validNodeIds = new Set(nodes.map((n) => n.id));
    const validNodes: SimulationNode[] = nodes.map((n) => {
      const existing = existingNodeMap.get(n.id);
      return {
        ...n,
        x: existing?.x ?? width / 2 + (Math.random() - 0.5) * 100,
        y: existing?.y ?? height / 2 + (Math.random() - 0.5) * 100,
        vx: existing?.vx ?? 0,
        vy: existing?.vy ?? 0,
        fx: existing?.fx,
        fy: existing?.fy,
      };
    });

    nodesRef.current = validNodes;
    const nodeMap = new Map(validNodes.map((n) => [n.id, n]));

    const validLinks: SimulationLink[] = edges
      .filter((e) => validNodeIds.has(e.sourceId) && validNodeIds.has(e.targetId))
      .map((e) => ({
        ...e,
        source: nodeMap.get(e.sourceId)!,
        target: nodeMap.get(e.targetId)!,
      }));

    const simulation = forceSimulation<SimulationNode>(validNodes)
      .force('link', forceLink<SimulationNode, SimulationLink>(validLinks).id((d) => d.id).distance(120))
      .force('charge', forceManyBody().strength(-400))
      .force('collide', forceCollide().radius(60))
      .force('center', forceCenter(width / 2, height / 2))
      .on('tick', () => {
        setSimNodes([...validNodes]);
        setSimLinks([...validLinks]);
      });

    simulationRef.current = simulation;
    return () => {
      simulation.stop();
    };
  }, [nodes, edges, width, height]);

  // Handle Dragging
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const dx = e.clientX - lastMousePos.current.x;
    const dy = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };

    if (draggedNode.current && simulationRef.current) {
      // Calculate coordinates relative to the transformed SVG group, accounting for container position
      draggedNode.current.fx = (e.clientX - rect.left - transform.x) / transform.scale;
      draggedNode.current.fy = (e.clientY - rect.top - transform.y) / transform.scale;
      simulationRef.current.alphaTarget(0.3).restart();
    } else if (isDraggingBackground.current) {
      setTransform((t) => ({ ...t, x: t.x + dx, y: t.y + dy }));
    }
  }, [transform]);

  const handleMouseUp = useCallback(() => {
    if (draggedNode.current && simulationRef.current) {
      draggedNode.current.fx = null;
      draggedNode.current.fy = null;
      simulationRef.current.alphaTarget(0);
    }
    draggedNode.current = null;
    isDraggingBackground.current = false;
    window.removeEventListener('mousemove', handleMouseMove as unknown as EventListener);
    window.removeEventListener('mouseup', handleMouseUp as unknown as EventListener);
  }, [handleMouseMove]);

  const handleMouseDown = (e: React.MouseEvent, node?: SimulationNode) => {
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    if (node) {
      draggedNode.current = node;
    } else {
      isDraggingBackground.current = true;
    }
    window.addEventListener('mousemove', handleMouseMove as unknown as EventListener);
    window.addEventListener('mouseup', handleMouseUp as unknown as EventListener);
  };

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const scaleChange = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(5, transform.scale * scaleChange));

    // Zoom centered on mouse
    const container = containerRef.current as unknown as HTMLDivElement;
    const rect = container?.getBoundingClientRect();
    if (rect) {
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const dx = (mouseX - transform.x) / transform.scale;
      const dy = (mouseY - transform.y) / transform.scale;

      setTransform({
        x: mouseX - dx * newScale,
        y: mouseY - dy * newScale,
        scale: newScale,
      });
    }
  }, [transform]);

  const zoomIn = () => {
    setTransform((t) => ({ ...t, scale: Math.min(5, t.scale * 1.2) }));
  };

  const zoomOut = () => {
    setTransform((t) => ({ ...t, scale: Math.max(0.1, t.scale / 1.2) }));
  };

  const fitView = useCallback(() => {
    if (!simNodes.length) return;

    const targets = selectedNodeIds.length > 0
      ? simNodes.filter((n) => selectedNodeIds.includes(n.id))
      : simNodes;

    if (!targets.length) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    targets.forEach((n) => {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x);
      maxY = Math.max(maxY, n.y);
    });

    const padding = 100;
    const graphWidth = maxX - minX + padding * 2;
    const graphHeight = maxY - minY + padding * 2;

    const scale = Math.min(width / graphWidth, height / graphHeight, 1.5);
    const x = width / 2 - ((minX + maxX) / 2) * scale;
    const y = height / 2 - ((minY + maxY) / 2) * scale;

    setTransform({ x, y, scale });
  }, [simNodes, selectedNodeIds, width, height]);

  const reorderGraph = () => {
    if (!simulationRef.current) return;

    // Identify root (core nodes go to the top)
    const rootNodes = simNodes.filter((n) => n.isCore);
    const primaryRoot = rootNodes[0] || simNodes[0];
    if (!primaryRoot) return;

    // Calculate "depth" from primary root for a simple hierarchy
    const depths = new Map<string, number>();
    depths.set(primaryRoot.id, 0);

    // Simple BFS to find depth
    const queue = [primaryRoot.id];
    const visited = new Set([primaryRoot.id]);

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const currentDepth = depths.get(currentId)!;

      simLinks.forEach((link) => {
        if (link.source.id === currentId && !visited.has(link.target.id)) {
          visited.add(link.target.id);
          depths.set(link.target.id, currentDepth + 1);
          queue.push(link.target.id);
        } else if (link.target.id === currentId && !visited.has(link.source.id)) {
          visited.add(link.source.id);
          depths.set(link.source.id, currentDepth + 1);
          queue.push(link.source.id);
        }
      });
    }

    // Apply a hierarchy force
    simulationRef.current
      .force('y', forceY<SimulationNode>((d) => {
        const depth = depths.get(d.id) ?? 2;
        return height * (0.2 + depth * 0.25);
      }))
      .force('x', forceCenter(width / 2, height / 2))
      .alpha(1)
      .restart();

    // After a short delay, remove the hierarchy forces and fit view
    setTimeout(() => {
      if (simulationRef.current) {
        simulationRef.current.force('y', null);
      }
      fitView();
    }, 1500);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container && typeof container.addEventListener === 'function') {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      if (container && typeof container.removeEventListener === 'function') {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleWheel]);

  return (
    <Box
      flex={1}
      bg="$white"
      overflow="hidden"
      onMouseDown={(e) => handleMouseDown(e)}
      // @ts-expect-error - for web compatibility
      ref={containerRef}
      // eslint-disable-next-line react-native/no-inline-styles, @typescript-eslint/no-explicit-any
      style={{ cursor: isDraggingBackground.current ? 'grabbing' : 'grab' } as any}
      position="relative"
    >
      {/* Controls Overlay */}
      <VStack
        position="absolute"
        top="$4"
        right="$4"
        space="sm"
        zIndex={10}
      >
        <Button size="sm" variant="outline" bg="$white" onPress={zoomIn}>
          <ButtonIcon as={ZoomIn} />
        </Button>
        <Button size="sm" variant="outline" bg="$white" onPress={zoomOut}>
          <ButtonIcon as={ZoomOut} />
        </Button>
        <Button size="sm" variant="outline" bg="$white" onPress={fitView}>
          <ButtonIcon as={Maximize} />
        </Button>
        <Button size="sm" variant="outline" bg="$white" onPress={reorderGraph} action="secondary">
          <ButtonIcon as={Layout} />
          <ButtonText ml="$2">Reorder</ButtonText>
        </Button>
      </VStack>

      <Svg width={width} height={height}>
        <Rect width="100%" height="100%" fill="url(#grid)" />
        <Defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <Path d="M 40 0 L 0 0 0 40" fill="none" stroke="#F1F5F9" strokeWidth="1" />
          </pattern>
          <Marker id="arrowhead" markerWidth="10" markerHeight="7" refX="25" refY="3.5" orient="auto">
            <Path d="M0,0 L10,3.5 L0,7 Z" fill="#CBD5E1" />
          </Marker>
          <RadialGradient id="nodeGradient" cx="50%" cy="50%" r="50%">
            <Stop offset="0%" stopColor={brandBase} stopOpacity="1" />
            <Stop offset="100%" stopColor={brandBase} stopOpacity="0.7" />
          </RadialGradient>
        </Defs>

        <G transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}>
          {/* Edges */}
          {simLinks.map((link) => (
            <G key={`link-${link.source.id}-${link.target.id}`}>
              <Line
                x1={link.source.x}
                y1={link.source.y}
                x2={link.target.x}
                y2={link.target.y}
                stroke="#E2E8F0"
                strokeWidth="2"
                opacity="0.6"
                markerEnd="url(#arrowhead)"
              />
              <SvgText
                x={(link.source.x + link.target.x) / 2}
                y={(link.source.y + link.target.y) / 2 - 10}
                fontSize="10"
                fontFamily={fontFamily}
                fill={textSubtle}
                textAnchor="middle"
                pointerEvents="none"
              >
                {link.relation}
              </SvgText>
            </G>
          ))}

          {/* Nodes */}
          {simNodes.map((node) => {
            const isSelected = selectedNodeIds.includes(node.id);
            const radius = isSelected ? 18 : 12;

            return (
              <G
                key={node.id}
                onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, node); }}
                onClick={() => onToggleNode(node.id)}
                // eslint-disable-next-line react-native/no-inline-styles, @typescript-eslint/no-explicit-any
                style={{ cursor: 'pointer' } as any}
              >
                {isSelected && <Circle cx={node.x} cy={node.y} r={radius + 15} fill={brandBase} opacity="0.1" />}
                <Circle
                  cx={node.x}
                  cy={node.y}
                  r={radius}
                  fill={isSelected ? 'url(#nodeGradient)' : 'white'}
                  stroke={isSelected ? brandBase : '#CBD5E1'}
                  strokeWidth={isSelected ? 4 : 2}
                />
                {/* Text Shadow/Halo for readability */}
                <SvgText
                  x={node.x}
                  y={node.y + radius + 22}
                  fontSize={isSelected ? 14 : 12}
                  fontWeight={isSelected ? 'bold' : '500'}
                  fontFamily={fontFamily}
                  fill="white"
                  stroke="white"
                  strokeWidth="4"
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  {node.label}
                </SvgText>
                <SvgText
                  x={node.x}
                  y={node.y + radius + 22}
                  fontSize={isSelected ? 14 : 12}
                  fontWeight={isSelected ? 'bold' : '500'}
                  fontFamily={fontFamily}
                  fill={isSelected ? textHeading : textSubtle}
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  {node.label}
                </SvgText>
              </G>
            );
          })}
        </G>
      </Svg>
    </Box>
  );
};
